import { useEffect, useMemo, useRef, useState } from 'react'
import { CardDragHandle } from '@/shared/components/card-drag-handle'
import { useTranslation } from 'react-i18next'
import {
  Camera,
  AlertTriangle,
  CheckCircle2,
  Clock,
  X,
  Plus,
  Plane,
} from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import {
  useLabRequests,
  useClaimChecklist,
  useRequestChecklist,
  type ClaimChecklistItem,
  type LabRequest,
} from '@/admin/hooks/use-lab-tasks'
import { useFormatters } from '@/shared/lib/formatting'
import { ChecklistRequestFields } from '@/admin/components/claims/checklist-request-fields'

// WPS-142 (Patrick Hill, 2026-09-17): the "Request Inspection" / "Request
// Drone Inspection" action was removed here. The client asked only for a
// SUGGESTION to consider a drone inspection - not a request button - so this
// panel now shows the informational drone-recommendation banner + the photo
// request history, and no inspection-request dialog. "Request Additional
// Photos" itself lives in the prominent claim header (WP1-59), served by
// ``RequestPhotosDialog`` below.

// Photo-request reasons are served by the API (claim checklist response) from
// the tenant-configurable ``lab_request.reason`` entity attribute, so they stay
// admin-editable in the Entity Editor with no hardcoded reason arrays here.

// WPS-142 #17: a light client-side check so the "Add" affordance and the
// per-field error can react; the backend re-validates every address.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

const STATUS_BADGES: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  PENDING: { label: 'Pending', variant: 'outline' },
  IN_PROGRESS: { label: 'In Progress', variant: 'default' },
  COMPLETED: { label: 'Completed', variant: 'secondary' },
  CANCELLED: { label: 'Cancelled', variant: 'destructive' },
}

interface Props {
  claimUuid: string
}

export function LabRequestPanel({ claimUuid }: Props) {
  const { t } = useTranslation()
  const { data: requests = [] } = useLabRequests(claimUuid)
  const { data: checklist } = useClaimChecklist(claimUuid)
  // "Request Additional Photos" now lives in the prominent claim header
  // (WP1-59); this panel keeps the request history + the drone suggestion.

  const photoRequests = requests.filter((r) => r.request_type === 'ADDITIONAL_PHOTOS')
  // Count only photo requests: inspection requests are no longer rendered in
  // this panel, so counting them would show a pending badge with no matching
  // row visible below.
  const pendingCount = photoRequests.filter((r) => r.status === 'PENDING' || r.status === 'IN_PROGRESS').length
  // WPS-142 #11 (Patrick Hill, 2026-09-17): photo categories requested
  // repeatedly but still deficient. The client asked for a SUGGESTION only, not
  // a request button - so this drives an informational banner recommending a
  // drone inspection, with no request-inspection action. The API only returns
  // these when the inspections flag is on and a category has crossed the
  // request threshold, so a non-empty list is the whole gate.
  const droneRecommendations = checklist?.drone_recommendations ?? []

  return (
    <div className="flex flex-col gap-3">
      {droneRecommendations.length > 0 && (
        <div className="flex items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--status-info-border)] bg-[var(--status-info-bg)] px-3 py-2.5">
          <Plane className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--status-info)]" />
          <div className="flex-1">
            <p className="text-[11px] font-medium text-[var(--ink)]">
              {t(
                'claims.droneRecommendationTitle',
                'Consider a drone inspection',
              )}
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--ink-2)]">
              {t(
                'claims.droneRecommendationBody',
                'These photos have been requested repeatedly but are still not sufficient. A drone inspection may obtain the required imagery and reduce back-and-forth with the claimant:',
              )}
            </p>
            <ul className="mt-1 flex flex-col gap-0.5">
              {droneRecommendations.map((r) => (
                <li key={r.category} className="text-[11px] text-[var(--ink-2)]">
                  {'• '}
                  <span className="font-medium text-[var(--ink)]">{r.label}</span>
                  {' '}
                  {t('claims.droneRecommendationCount', 'requested {{count}} times', {
                    count: r.request_count,
                  })}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CardDragHandle />
          <AlertTriangle className="h-3.5 w-3.5 text-[var(--ink-3)]" />
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
            Lab Requests
          </span>
          {pendingCount > 0 && (
            <Badge variant="outline" className="text-[9px]">
              {pendingCount} pending
            </Badge>
          )}
        </div>
      </div>

      {/* Existing requests */}
      {requests.length > 0 && (
        <div className="flex flex-col gap-2">
          {photoRequests.length > 0 && (
            <RequestGroup
              title="Photo Requests"
              icon={<Camera className="h-3 w-3" />}
              requests={photoRequests}
            />
          )}
        </div>
      )}

      {photoRequests.length === 0 && (
        <p className="text-[11px] text-[var(--ink-3)]">
          {t(
            'claims.noRequestsSent',
            'No requests sent. Use "Request Additional Photos" at the top of the claim to email the claimant a checklist.',
          )}
        </p>
      )}
    </div>
  )
}

function RequestGroup({
  title,
  icon,
  requests,
}: {
  title: string
  icon: React.ReactNode
  requests: LabRequest[]
}) {
  const { formatDate } = useFormatters()
  return (
    <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-3.5 py-2.5">
      <div className="mb-2 flex items-center gap-1.5">
        {icon}
        <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
          {title}
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        {requests.map((req) => {
          const badge = STATUS_BADGES[req.status] ?? STATUS_BADGES.PENDING
          return (
            <div
              key={req.uuid}
              className="flex items-start gap-2 text-[11px]"
            >
              {req.status === 'COMPLETED' ? (
                <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-green-600" />
              ) : (
                <Clock className="mt-0.5 h-3 w-3 shrink-0 text-[var(--ink-3)]" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-[var(--ink)]">
                    {req.reason_label}
                  </span>
                  <Badge variant={badge.variant} className="text-[9px]">
                    {badge.label}
                  </Badge>
                </div>
                {req.notes && (
                  <p className="mt-0.5 text-[var(--ink-3)]">{req.notes}</p>
                )}
                {req.response_notes && (
                  <p className="mt-0.5 text-[var(--ink-2)]">
                    <span className="font-medium">Response:</span> {req.response_notes}
                  </p>
                )}
                <p className="mt-0.5 text-[10px] text-[var(--ink-3)]">
                  {req.requested_by_name} · {formatDate(req.created_at)}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * WP1-59 Req 3 - Request Additional Photos.
 *
 * Lab selects which checklist items to request (checkboxes, dynamic per
 * defect) and adds an optional
 * customer-visible note. On submit the system emails the claim's MAIN CONTACT
 * a checklist scoped to exactly those items - no "notify the RS" relay.
 */
export function RequestPhotosDialog({
  claimUuid,
  onClose,
}: {
  claimUuid: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { data: checklist, isPending, isError } = useClaimChecklist(claimUuid)
  const requestChecklist = useRequestChecklist(claimUuid)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [note, setNote] = useState('')
  // WPS-142 #17: the "Email link to" recipients (homeowner + Contributor
  // collaborators), editable and multiple. First is the primary To; the rest
  // are CC'd on the same email (same upload link).
  const [recipients, setRecipients] = useState<string[]>([])
  const [emailDraft, setEmailDraft] = useState('')
  // Surface per-field errors only after a submit attempt, never a dead disabled
  // button (house rule: the primary action stays enabled for missing fields).
  const [attempted, setAttempted] = useState(false)

  const items = useMemo(() => checklist?.items ?? [], [checklist])
  // Photos come from the tenant-wide catalogue (every defect), so lab can
  // request a category beyond the current defect (WPS-59 Req 3). Documents
  // stay scoped to the claim's own defect (photos-only widening per Patrick).
  const photoCategories = useMemo(
    () => checklist?.all_photo_categories ?? [],
    [checklist],
  )
  // WPS-142 #9/#17: proof documents the request adds on its own. They render
  // ticked and locked; one missing from the checklist items still gets a row.
  const autoIncluded = useMemo(
    () => checklist?.auto_included_documents ?? [],
    [checklist],
  )
  const autoIncludedKeys = useMemo(
    () => new Set(autoIncluded.map((d) => d.key)),
    [autoIncluded],
  )
  const documentItems = useMemo(() => {
    const docs = items.filter((i) => i.kind === 'document')
    const listed = new Set(docs.map((d) => d.key))
    const extra: ClaimChecklistItem[] = autoIncluded
      .filter((d) => !listed.has(d.key))
      .map((d) => ({
        kind: 'document',
        key: d.key,
        label: d.label,
        description: '',
        is_required: false,
        category: d.key,
        satisfied: false,
        approved: false,
      }))
    return [...docs, ...extra]
  }, [items, autoIncluded])

  // WPS-142 #17: pre-check the defect's required items that have NOT yet been
  // received (photos required for this defect + documents), once the catalogue
  // loads, so a single click requests everything still outstanding while other
  // (non-defect) categories stay offered but unchecked, and already-received
  // items are left unchecked. Seeding is tracked per claim (not a one-shot flag)
  // so the pre-selection re-runs when the claim changes while the dialog stays
  // mounted (navigating between claims); otherwise the first claim's selection
  // would stick.
  const seededForClaim = useRef<string | null>(null)
  useEffect(() => {
    if (!checklist || seededForClaim.current === claimUuid) return
    seededForClaim.current = claimUuid
    setSelected(
      new Set([
        ...photoCategories
          .filter((c) => c.is_required_for_defect && !c.satisfied)
          .map((c) => c.key),
        ...documentItems.filter((i) => i.is_required && !i.satisfied).map((i) => i.key),
      ]),
    )
    // Prepopulate the "Email link to" list with the homeowner + collaborators.
    setRecipients(
      (checklist.default_recipients ?? [])
        .map((r) => r.email)
        .filter((e) => !!e),
    )
  }, [checklist, photoCategories, documentItems, claimUuid])

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const addRecipient = () => {
    const email = emailDraft.trim()
    if (!EMAIL_RE.test(email)) return
    setRecipients((prev) =>
      prev.some((e) => e.toLowerCase() === email.toLowerCase())
        ? prev
        : [...prev, email],
    )
    setEmailDraft('')
  }

  const removeRecipient = (email: string) => {
    setRecipients((prev) => prev.filter((e) => e !== email))
  }

  const photosMissing = selected.size === 0
  const recipientsMissing = recipients.length === 0

  const handleSubmit = () => {
    setAttempted(true)
    // Fold a typed-but-not-added draft into the list so a user who typed one
    // address and hit Send is not silently dropped.
    const draft = emailDraft.trim()
    const finalRecipients =
      draft && EMAIL_RE.test(draft) && !recipients.includes(draft)
        ? [...recipients, draft]
        : recipients
    if (
      requestChecklist.isPending ||
      photosMissing ||
      finalRecipients.length === 0
    )
      return
    requestChecklist.mutate(
      {
        mode: 'additional',
        requirement_keys: Array.from(selected),
        customer_note: note,
        recipient_emails: finalRecipients,
      },
      { onSuccess: () => onClose() },
    )
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-[14px]">
            {t(
              'claims.requestAdditionalDocuments',
              'Request additional documentation',
            )}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {t(
              'claims.requestAdditionalDocumentsHint',
              'The items not yet received are pre-selected. Adjust if needed - the system emails the claimant a checklist for exactly these items.',
            )}
          </DialogDescription>
        </DialogHeader>

        {/* WPS-130: the checklist checkboxes + note are the shared picker,
            rendered identically by the intake "Request More Documents" widget.
            The Lab request payload has no slot for free-text asks, so the
            "something else" section is omitted here (no customItems props). */}
        <ChecklistRequestFields
          documents={documentItems}
          photos={photoCategories}
          isPending={isPending}
          isError={isError}
          selectedKeys={selected}
          onToggleKey={toggle}
          autoIncludedKeys={autoIncludedKeys}
          photosLabel={t('claims.photoCategories', 'Photo categories')}
          note={note}
          onNoteChange={setNote}
        />
        {attempted && photosMissing && (
          <p className="px-1.5 text-[11px] text-[var(--status-error)]">
            {t(
              'claims.selectAtLeastOnePhoto',
              'Select at least one photo category to request.',
            )}
          </p>
        )}

        <div className="flex flex-col gap-3">
          {/* WPS-142 #17: "Email link to" - prepopulated with the homeowner +
              Contributor collaborators, editable, multiple. */}
          <div>
            <Label
              className={`mb-1 block text-[10px] font-medium uppercase tracking-[0.08em] ${
                attempted && recipientsMissing
                  ? 'text-[var(--status-error)]'
                  : 'text-[var(--ink-3)]'
              }`}
            >
              {t('claims.emailLinkTo', 'Email link to')}
            </Label>
            {recipients.length > 0 && (
              <div className="mb-1.5 flex flex-wrap gap-1.5">
                {recipients.map((email) => (
                  <span
                    key={email}
                    className="flex items-center gap-1 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-2 py-0.5 text-[11px] text-[var(--ink)]"
                  >
                    {email}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => removeRecipient(email)}
                      className="h-4 w-4 p-0 text-[var(--ink-3)] hover:text-[var(--status-error)]"
                      aria-label={t('claims.removeRecipient', 'Remove {{email}}', {
                        email,
                      })}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Input
                type="email"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addRecipient()
                  }
                }}
                placeholder={t(
                  'claims.addEmailPlaceholder',
                  'Add an email address',
                )}
                className={`h-8 text-[12px] ${
                  attempted && recipientsMissing
                    ? 'ring-1 ring-[var(--status-error)]'
                    : ''
                }`}
              />
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                onClick={addRecipient}
                disabled={!EMAIL_RE.test(emailDraft.trim())}
                aria-label={t('claims.addRecipient', 'Add recipient')}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            {attempted && recipientsMissing && (
              <p className="mt-1 text-[11px] text-[var(--status-error)]">
                {t(
                  'claims.addAtLeastOneRecipient',
                  'Add at least one email address to send the link to.',
                )}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={requestChecklist.isPending}
            onClick={handleSubmit}
          >
            {requestChecklist.isPending ? 'Sending…' : 'Send checklist email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
